// src/components/users/UserManagement.jsx
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useUserManagement } from "../../hooks/useUserManagement";
import { companyService } from "../../services/companyService";
import { departmentService } from "../../services/departmentService";
import { locationService } from "../../services/locationService";
import { normalizeRole, ROLES } from "../../utils/roles";

// MUI Components
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Alert,
  InputAdornment,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Fade,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  Collapse,
} from "@mui/material";

// Icons
import AddIcon           from "@mui/icons-material/Add";
import SearchIcon        from "@mui/icons-material/Search";
import ClearIcon         from "@mui/icons-material/Clear";
import EditIcon          from "@mui/icons-material/Edit";
import DeleteIcon        from "@mui/icons-material/Delete";
import MoreVertIcon      from "@mui/icons-material/MoreVert";
import FileDownloadIcon  from "@mui/icons-material/FileDownload";
import WarningAmberIcon  from "@mui/icons-material/WarningAmber";
import FilterAltIcon     from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon  from "@mui/icons-material/FilterAltOff";
import ExpandMoreIcon    from "@mui/icons-material/ExpandMore";
import ExpandLessIcon    from "@mui/icons-material/ExpandLess";

// Modals & Common Components
import CreateEditUserModal     from "../Modals/CreateEditUserModal";
import DeleteConfirmationModal from "../Modals/UserDeleteConfirmationModal";
import ToastNotification       from "../common/ToastNotification";
import PermissionDenied        from "../common/PermissionDenied";

/* ── Pure Helpers ────────────────────────────────────────────────────────── */
const getRoleColor = (role) => {
  const map = {
    [ROLES.SUPERADMIN]:     "error",
    [ROLES.ADMIN]:          "warning",
    [ROLES.LOCATION_ADMIN]: "info",
       [ROLES.AUDITOR]:        "success",
    [ROLES.USER]:           "default",
    [ROLES.TECHNICIAN]:     "secondary",
  };
  return map[normalizeRole(role)] || "default";
};

const exportToCSV = (filteredUsers, lookups) => {
  const headers = [
    "Name", "Email", "Role", "Company", "Company ID",
    "Department", "Location", "Description",
  ];
  const rows = filteredUsers.map((u) => [
    u.name                            || "",
    u.email                           || "",
    u.role                            || "",
    lookups.companyName(u.companyId)  || "",
    u.companyId                       || "",
    lookups.departmentName(u.departmentId) || "",
    lookups.locationName(u.locationCode)   || "",
    u.description                     || "",
  ]);
  const csv = [headers, ...rows]
    .map((row) =>
      row
        .map((v) =>
          String(v).includes(",") || String(v).includes('"')
            ? `"${String(v).replace(/"/g, '""')}"`
            : v
        )
        .join(",")
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute(
    "download",
    `Users_Export_${new Date().toISOString().split("T")[0]}.csv`
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/* ── Skeleton Loader ─────────────────────────────────────────────────────── */
const UserTableSkeleton = () => (
  <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
      <Box>
        <Skeleton variant="text" width={220} height={40} />
        <Skeleton variant="text" width={340} height={20} />
      </Box>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Skeleton variant="rounded" width={130} height={40} />
        <Skeleton variant="rounded" width={130} height={40} />
      </Box>
    </Box>
    <Box sx={{ mb: 2 }}>
      <Skeleton variant="rounded" width="40%" height={40} />
    </Box>
    <Box
      sx={{
        background: "#fff",
        borderRadius: "12px",
        p: 2.5,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          py: 1.5,
          mb: 1,
          borderBottom: "2px solid #e2e8f0",
        }}
      >
        {[60, 160, 200, 120, 120, 120, 80, 160, 60].map((w, i) => (
          <Skeleton key={i} variant="text" width={w} height={16} />
        ))}
      </Box>
      {Array.from({ length: 8 }, (_, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            gap: 2,
            py: 1.5,
            alignItems: "center",
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <Skeleton variant="text" width={40} height={14} />
          <Skeleton variant="text" width={120} height={14} />
          <Skeleton variant="text" width={180} height={14} />
          <Skeleton variant="text" width={100} height={14} />
          <Skeleton variant="text" width={100} height={14} />
          <Skeleton variant="text" width={80} height={14} />
          <Skeleton variant="rounded" width={70} height={24} />
          <Skeleton variant="text" width={120} height={14} />
          <Skeleton variant="circular" width={30} height={30} />
        </Box>
      ))}
    </Box>
  </Container>
);

/* ── Main Component ──────────────────────────────────────────────────────── */
const UserManagement = () => {
  const { currentUser } = useAuth();
  const {
    users,
    totalCount,
    loading,
    deleteLoading,
    permissionError,
    error,
    fetchUsers,
    searchUsers,
    deleteUser,
    isSelfDelete,
    filters,
    setFilters,
    clearFilters,
  } = useUserManagement();

  // Local UI State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal,   setShowEditModal]   = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser,    setSelectedUser]    = useState(null);
  const [searchTerm,      setSearchTerm]      = useState("");
  const [notification,    setNotification]    = useState(null);

  // Filters panel open/closed
  const [filtersOpen, setFiltersOpen] = useState(true);

  // Pagination & Menu State
  const [anchorEl,       setAnchorEl]       = useState(null);
  const [menuTargetUser, setMenuTargetUser] = useState(null);
  const [page,           setPage]           = useState(0);
  const [rowsPerPage,    setRowsPerPage]    = useState(10);

  // FK lookup lists → display names
  const [companies,   setCompanies]   = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations,   setLocations]   = useState([]);

  const callerRole = useMemo(
    () => normalizeRole(currentUser?.role),
    [currentUser]
  );

  // Load lookup lists once per user/role
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;

    (async () => {
      try {
        // For SuperAdmin  resolve names for users across companies.
        // For everyone else the services scope to the caller's company.
        const deptArgs =
          callerRole === ROLES.SUPERADMIN ? {} : { companyId: currentUser.companyId };
        const locArgs =
          callerRole === ROLES.SUPERADMIN ? {} : { companyId: currentUser.companyId };

         // 1. Fetch the current user's company directly if they aren't a superadmin
        let currentCo = null;
        if (callerRole !== ROLES.SUPERADMIN && currentUser.companyId) {
          currentCo = await companyService.getCompanyById(currentUser.companyId).catch(() => null);
        }

        const [cos, deps, locs] = await Promise.all([
          callerRole === ROLES.SUPERADMIN
            ? companyService.getAllCompanies(currentUser)
            : Promise.resolve(currentCo ? [currentCo] : []),
          departmentService.fetchDepartments(deptArgs),
          locationService.fetchLocations(locArgs),
        ]);
        if (cancelled) return;
        setCompanies(cos || []);
        setDepartments(deps || []);
        setLocations(locs || []);
      } catch (e) {
        console.error("[UserManagement] Lookup load failed:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser, callerRole]);

  // Build lookup maps once per list change
  const companyMap = useMemo(
    () => Object.fromEntries((companies || []).map(c => [String(c.companyId || c.id), c.name])),
    [companies]
  );
  const departmentMap = useMemo(
    () => Object.fromEntries((departments || []).map(d => [String(d.id), d.departmentName || d.name])),
    [departments]
  );
  const locationMap = useMemo(
    () => Object.fromEntries((locations || []).map(l => [String(l.locationCode || l.id), l.locationName || l.name])),
    [locations]
  );

  const lookups = useMemo(() => ({
    companyName:    (id)   => companyMap[String(id)]    || "—",
    departmentName: (id)   => departmentMap[String(id)] || "—",
    locationName:   (code) => locationMap[String(code)] || "—",
  }), [companyMap, departmentMap, locationMap]);

  const showNotification = useCallback((message, type = "success") => {
    setNotification({ message, type });
  }, []);

  // Server already paginates/sorts/filters — no client filtering here.
  const filteredUsers = users;

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        filters.companyId ||
          filters.role ||
          filters.isActive === true ||
          filters.isActive === false ||
          filters.sortField !== "name" ||
          filters.sortOrder !== "asc"
      ),
    [filters]
  );

  const handleUserSaveSuccess = useCallback(
    (message) => {
      showNotification(message, "success");
      setShowCreateModal(false);
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers(searchTerm, page, rowsPerPage);
    },
    [showNotification, fetchUsers, searchTerm, page, rowsPerPage]
  );

  const isDeletingCompanyUser = (user) =>
    currentUser?.companyId === user?.companyId && !isSelfDelete(user);

  const handleDeleteConfirm = async () => {
    try {
      const { isSelfDelete: selfDelete } = await deleteUser(selectedUser);
      if (selfDelete) {
        showNotification(
          "Your account has been deleted. Logging out...",
          "success"
        );
      } else {
        showNotification("User deleted successfully");
      }
    } catch (err) {
      showNotification(`Delete error: ${err.message}`, "error");
    } finally {
      setShowDeleteModal(false);
      setSelectedUser(null);
    }
  };

  const handleExportCSV = () => {
    if (filteredUsers.length === 0) {
      showNotification("No users to export.", "warning");
      return;
    }
    exportToCSV(filteredUsers, lookups);
    showNotification("Users exported successfully.");
  };

  const handleMenuOpen = (e, user) => {
    setAnchorEl(e.currentTarget);
    setMenuTargetUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuTargetUser(null);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPage(0);
    searchUsers(value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setPage(0);
    fetchUsers("", 0, rowsPerPage);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    fetchUsers(searchTerm, newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (e) => {
    const newRpp = parseInt(e.target.value, 10);
    setRowsPerPage(newRpp);
    setPage(0);
    fetchUsers(searchTerm, 0, newRpp);
  };

  const handleClearFilters = () => {
    clearFilters();
    setPage(0);
  };

  // Single source of truth for fetching — fires on mount AND
  // whenever filters change.
  useEffect(() => {
    setPage(0);
    fetchUsers(searchTerm, 0, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  if (permissionError) {
    return (
      <Box sx={{ p: 4 }}>
        <PermissionDenied message={permissionError} />
      </Box>
    );
  }

  if (loading && users.length === 0) {
    return <UserTableSkeleton />;
  }

  return (
    <Container
      maxWidth="xl"
      sx={{ mt: 4, mb: 4, display: "flex", flexDirection: "column", gap: 3 }}
    >
      {/* ── Error Banner ── */}
      {error && (
        <Alert
          severity="error"
          onClose={() => fetchUsers(searchTerm, page, rowsPerPage)}
          sx={{ borderRadius: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              letterSpacing: "-0.5px",
            }}
          >
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create, manage, and control user access across your organisation
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateModal(true)}
            disableElevation
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
            }}
          >
            Create User
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCSV}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* ── Filters + Search (merged) ── */}
      <Paper
        sx={{ p: 2.5, borderRadius: 3, bgcolor: "background.paper" }}
        variant="outlined"
      >
        {/* Header row: title, search-count, hide/show toggle */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: filtersOpen ? 1.5 : 0,
          }}
        >
          <FilterAltIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={700}>
            Filters
          </Typography>

          {searchTerm && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              Found <strong>{totalCount}</strong> user
              {totalCount !== 1 ? "s" : ""} matching{" "}
              <em>"{searchTerm}"</em>
            </Typography>
          )}

          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            {hasActiveFilters && (
              <Chip
                label="Clear all"
                size="small"
                variant="outlined"
                icon={<FilterAltOffIcon fontSize="small" />}
                onClick={handleClearFilters}
              />
            )}
            <Button
              size="small"
              variant="text"
              onClick={() => setFiltersOpen((v) => !v)}
              startIcon={filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                minWidth: 0,
              }}
            >
              {filtersOpen ? "Hide" : "Show"}
            </Button>
          </Box>
        </Box>

        <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {/* Search — first item, wider */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Search Users"
                placeholder="Search by name, email, company ID, location…"
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ color: "action.active" }}
                        fontSize="small"
                      />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClearSearch}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>

            {/* Company — SuperAdmin only */}
            {callerRole === ROLES.SUPERADMIN && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Company ID"
                  value={filters.companyId}
                  onChange={(e) => setFilters({ companyId: e.target.value })}
                  placeholder="e.g. PRL"
                />
              </Grid>
            )}

            {/* Role */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  label="Role"
                  value={filters.role}
                  onChange={(e) => setFilters({ role: e.target.value })}
                >
                  <MenuItem value="">All roles</MenuItem>
                  <MenuItem value={ROLES.SUPERADMIN}>Super Admin</MenuItem>
                  <MenuItem value={ROLES.ADMIN}>Admin</MenuItem>
                  <MenuItem value={ROLES.LOCATION_ADMIN}>
                    Location Admin
                  </MenuItem>
                                   <MenuItem value={ROLES.AUDITOR}>Auditor</MenuItem>
                  <MenuItem value={ROLES.TECHNICIAN}>Technician</MenuItem>
                  <MenuItem value={ROLES.USER}>User</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={filters.isActive === "" ? "" : String(filters.isActive)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFilters({ isActive: v === "" ? "" : v === "true" });
                  }}
                >
                  <MenuItem value="">All statuses</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Sort */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort by</InputLabel>
                <Select
                  label="Sort by"
                  value={`${filters.sortField}:${filters.sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split(":");
                    setFilters({ sortField: field, sortOrder: order });
                  }}
                >
                  <MenuItem value="name:asc">Name (A→Z)</MenuItem>
                  <MenuItem value="name:desc">Name (Z→A)</MenuItem>
                  <MenuItem value="email:asc">Email (A→Z)</MenuItem>
                  <MenuItem value="email:desc">Email (Z→A)</MenuItem>
                  <MenuItem value="role:asc">Role (A→Z)</MenuItem>
                  <MenuItem value="createdAt:desc">Newest first</MenuItem>
                  <MenuItem value="createdAt:asc">Oldest first</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      {/* ── Table ── */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderRadius: 3,
          boxShadow: "0px 4px 16px rgba(0,0,0,0.02)",
          overflowX: "auto",
          "&::-webkit-scrollbar": { height: 8 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(0,0,0,0.12)",
            borderRadius: 4,
          },
        }}
      >
        {filteredUsers.length === 0 ? (
          <Box sx={{ p: 8, textAlign: "center" }}>
            <Alert
              severity="info"
              sx={{ justifyContent: "center", borderRadius: 2.5 }}
            >
              No users found. {hasActiveFilters || searchTerm
                ? "Try adjusting your filters or search."
                : "Create one to get started."}
            </Alert>
          </Box>
        ) : (
          <Table sx={{ minWidth: 1000 }} size="medium">
            <TableHead>
              <TableRow>
                {[
                  "Sl.No",
                  "User",
                  "Email",
                  "Company",
                  "Company ID",
                  "Department",
                  "Role",
                  "Description",
                  "Actions",
                ].map((h, idx) => (
                  <TableCell
                    key={idx}
                    align={h === "Sl.No" || h === "Actions" ? "center" : "left"}
                    sx={{
                      fontWeight: 700,
                      bgcolor: "background.neutral",
                      color: "text.primary",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredUsers.map((row, index) => {
                const selfDelete = isSelfDelete(row);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      ...(selfDelete && {
                        bgcolor: "rgba(211,47,47,0.03)",
                        "&:hover": {
                          bgcolor: "rgba(211,47,47,0.07) !important",
                        },
                      }),
                    }}
                  >
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="text.secondary"
                      >
                        {page * rowsPerPage + index + 1}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="text.primary"
                        >
                          {row.name || "—"}
                        </Typography>
                        {selfDelete && (
                          <Chip
                            label="You"
                            size="small"
                            color="error"
                            variant="outlined"
                            icon={
                              <WarningAmberIcon style={{ fontSize: 12 }} />
                            }
                            sx={{
                              height: 18,
                              fontSize: "0.6rem",
                              fontWeight: 700,
                              "& .MuiChip-label": { px: 0.75 },
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace", fontSize: "0.82rem" }}
                      >
                        {row.email || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {lookups.companyName(row.companyId)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                      >
                        {row.companyId || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {lookups.departmentName(row.departmentId)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={row.role || "N/A"}
                        size="small"
                        color={getRoleColor(row.role)}
                        sx={{ fontWeight: 600, fontSize: "0.72rem" }}
                      />
                    </TableCell>

                    <TableCell sx={{ maxWidth: 200 }}>
                      <Tooltip
                        title={row.description || ""}
                        arrow
                        disableHoverListener={
                          !row.description || row.description.length <= 50
                        }
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 180,
                          }}
                        >
                          {row.description
                            ? row.description.length > 50
                              ? `${row.description.slice(0, 50)}...`
                              : row.description
                            : "—"}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, row)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* ── Pagination ── */}
      {totalCount > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Showing {totalCount === 0 ? 0 : page * rowsPerPage + 1} to{" "}
            {Math.min((page + 1) * rowsPerPage, totalCount)} of {totalCount}{" "}
            users
          </Typography>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ border: "none", overflow: "hidden" }}
          />
        </Box>
      )}

      {/* ── Context Menu ── */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            minWidth: 200,
            borderRadius: 2.5,
            boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setSelectedUser(menuTargetUser);
            setShowEditModal(true);
            handleMenuClose();
          }}
          sx={{ gap: 1.5, py: 1 }}
        >
          <EditIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={500}>
            Edit User
          </Typography>
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={() => {
            setSelectedUser(menuTargetUser);
            setShowDeleteModal(true);
            handleMenuClose();
          }}
          sx={{ gap: 1.5, py: 1, color: "error.main" }}
        >
          <DeleteIcon fontSize="small" color="error" />
          <Typography variant="body2" fontWeight={600} color="error.main">
            {isSelfDelete(menuTargetUser) ? "Delete My Account" : "Delete User"}
          </Typography>
        </MenuItem>
      </Menu>

      {/* ── Toast ── */}
      {notification && (
        <ToastNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* ── Modals ── */}
      {showCreateModal && (
        <CreateEditUserModal
          open={showCreateModal}
          user={null}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleUserSaveSuccess}
          currentUserRole={callerRole}
        />
      )}

      {showEditModal && selectedUser && (
        <CreateEditUserModal
          open={showEditModal}
          user={selectedUser}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSuccess={handleUserSaveSuccess}
          currentUserRole={callerRole}
        />
      )}

      {showDeleteModal && selectedUser && (
        <DeleteConfirmationModal
          itemName={selectedUser.name || selectedUser.email}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedUser(null);
          }}
          onConfirm={handleDeleteConfirm}
          loading={deleteLoading}
          isDeletingSelf={isSelfDelete(selectedUser)}
          isDeletingCompanyUser={isDeletingCompanyUser(selectedUser)}
        />
      )}
    </Container>
  );
};

export default UserManagement;